<template>
  <div class="flex flex-col h-full">
    <div class="flex justify-between items-center mb-4">
      <div class="text-sm lg:text-xl">DAO Treasury</div>
      <div>
        <div class="w-full space-x-2 flex">
          <div
            class="topa-btn px-2 text-xs font-bold w-14 h-8"
            :class="{
              'topa-btn__active': state.selected == i,
            }"
            v-for="(text, i) in ['1M', '1Y', 'ALL']"
            :key="i"
            @click="state.selected = i"
          >
            {{ text }}
          </div>
        </div>
      </div>
    </div>
    <div>
      <CheckBox v-model="state.checked">
        <div class="text-xs lg:text-sm">
          Native Token
          <span v-if="state.rate != 0"
            >({{ (state.rate * 100).toFixed(2) }}%)</span
          >
        </div>
      </CheckBox>
    </div>
    <div class="flex-1 min-h-0 h-36 lg:h-full" ref="container">
      <VChart
        ref="chart"
        v-if="!loading"
        class="w-full h-36 lg:h-full"
        :option="option"
        :key="width"
      ></VChart>
      <div v-else class="w-full h-full flex justify-center items-center">
        <Spin></Spin>
      </div>
    </div>
  </div>
</template>

<script setup>
import { queryTreasuryChart, queryTreasuryChartEco } from '@/api/dao';
import CheckBox from '@/components/CheckBox.vue';
import Spin from '@/components/Spin.vue';
import { useReqByBool } from '@/hooks';
import { formatVal } from '@/utils';
import { useElementSize } from '@vueuse/core';
import * as echarts from 'echarts';
import { LineChart } from 'echarts/charts';
import {
  GraphicComponent,
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
} from 'echarts/components';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { ref, computed, inject, onMounted, reactive, watch } from 'vue';
import VChart from 'vue-echarts';
import { homeChains, homeTypes } from '../../constant';

const props = defineProps({
  ecoCode: String,
});
const chart = ref(null);

const container = ref(null);
const { width, height } = useElementSize(container);
use([
  CanvasRenderer,
  LineChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  GraphicComponent,
]);

const homeState = inject('homeState');

const state = reactive({
  list: [],
  rate: 0,
  selected: 0,
  checked: true,
});

const list = computed(() => {
  const arr = JSON.parse(JSON.stringify(state.list));
  arr.reverse();
  return (
    state.selected == 0
      ? arr.slice(0, 30)
      : state.selected == 1
      ? arr.slice(0, 365)
      : arr
  ).reverse();
});

const option = computed(() => {
  let min = list.value[0]?.nftTreasury;

  list.value.forEach((x) => {
    min = Math.min(min, x?.nftTreasury);
    min = Math.min(min, x?.ftTreasury);
  });

  return {
    tooltip: {
      trigger: 'axis',
      borderColor: '#FFFFFF66',
      backgroundColor: '#1e1f21bb',
      textStyle: {
        color: '#fff',
        fontSize: 12,
      },
      axisPointer: {
        type: 'cross',
        label: {
          show: false,
          backgroundColor: '#6a7985',
        },
      },
      formatter: (conf) => {
        const info = list.value[conf[0].dataIndex];

        return `
          <div class="space-y-1">
            <div>${info.day}</div>
            <div class="flex items-center">
              Total: $${formatVal(info.ftTreasury + info.nftTreasury, {
                omit: true,
              })}
            </div>
            <div class="flex items-center">
              <div class="w-3 h-3 rounded mr-2" style="background: #9317b5ff"></div>
              NFT value: ${formatVal(info.nftTreasury, { omit: true })}
            </div>
            <div class="flex items-center">
              <div class="w-3 h-3 rounded mr-2" style="background: #f72585ff"></div>
              FT value: ${formatVal(info.ftTreasury, { omit: true })}</div>
              <div class="flex items-center">
              <div class="w-3 h-3 rounded mr-2" style="background: #fff"></div>
              NTR: ${(info.nativeTokenRatio * 100).toFixed(2)}%</div>
          </div>
        `;
      },
    },
    legend: {
      data: ['NFT value', 'FT value'],
      right: 'left',
      // left: 'left',
      top: 'top',
      // icon:'rect',
      textStyle: {
        color: '#fff',
      },
    },
    grid: {
      top: '15%',
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true,
    },
    xAxis: [
      {
        data: list.value.map((x) => x.day),
        type: 'category',
        splitLine: {
          show: false,
        },
        axisLine: {
          lineStyle: {
            color: '#fff',
          },
        },
        axisLabel: {
          color: '#ffffff4d',
          // formatter: (value) => formatVal(value, { omit: true }),
        },
      },
    ],
    yAxis: [
      {
        type: 'value',
        splitLine: {
          show: false,
        },
        axisLine: {
          lineStyle: {
            color: '#fff',
          },
        },
        axisLabel: {
          color: '#ffffff4d',
          formatter: (value) => formatVal(value, { omit: true }),
        },
        // min,
      },
    ],
    color: ['#9317B5FF', '#F72585FF'],
    series: [
      {
        name: 'NFT value',
        type: 'line',
        stack: 'Total',
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: '#9317B5FF' },
            { offset: 1, color: '#F7258500' },
          ]),
        },
        emphasis: {
          focus: 'series',
        },
        data: list.value.map((x) => x.nftTreasury),
        showSymbol: false,
      },
      {
        name: 'FT value',
        type: 'line',
        stack: 'Total',
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: '#F72585FF' },
            { offset: 1, color: '#F7258500' },
          ]),
        },
        emphasis: {
          focus: 'series',
        },
        data: list.value.map((x) => x.ftTreasury),
        showSymbol: false,
      },
    ],
  };
});

const { loadData, loading } = useReqByBool(async () => {
  const res = props.ecoCode
    ? await queryTreasuryChartEco(props.ecoCode)
    : await queryTreasuryChart(
        homeTypes[homeState?.selectedType].value,
        homeChains[homeState?.selectedChain].value,
        homeState?.filterStatus,
        state.checked,
      );

  console.log(res);

  state.list = res;
  if (res.length > 0) state.rate = res[res.length - 1].nativeTokenRatio;
});

watch(
  () => [
    homeState?.selectedType,
    homeState?.selectedChain,
    homeState?.filterStatus,
    state.checked,
  ],
  loadData,
);

onMounted(loadData);
</script>

<style scoped>
.topa-btn {
  height: 32px;
  border-radius: 8px;
  border: 1px solid #2a2e33;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
}
.topa-btn__active {
  background: #ff5585ff;
  border: 1px solid #ff5585ff;
}
</style>
